import { CSSProperties } from 'react';
import { useDispatch, useReduxState } from 'hooks';
import { Button, ConfirmButton } from 'components/common';
import { retryCourseCompletion } from 'lib/reader';
import { ReaderCompletionNotification } from '.';

import type { ReaderSettings } from 'shared-types';
import type { ButtonSize } from 'components/common/Button';

const ReaderCompletionRetry = ({ style }: { style?: CSSProperties }) => {
  const {
    messages,
    pageState: {
      chapterSlug,
      completion,
      presentMode,
      readOnly,
      readerCourseData: { course, toc },
      settings,
      settingsPresentMode
    }
  } = useReduxState();

  const { dispatch } = useDispatch();

  const handleRetry = () =>
    retryCourseCompletion({
      chapterSlug,
      courseSlug: course.slug,
      dispatch,
      firstChapterSlug: toc.chapters[toc.chaptersOrder[0]].slug
    });

  if (
    readOnly ||
    !completion ||
    completion.status !== 'failed' ||
    !course?.completionAllowRetries
  ) {
    return null;
  }

  const currentSettings =
    (presentMode ? settingsPresentMode : settings) || ({} as ReaderSettings);

  return (
    <ReaderCompletionNotification
      icon="exclamation-circle"
      description={messages.readerCompletionFailedNotification}
      style={style}
    >
      {completion.tasksProgress * 100 < course.completionTasks ? (
        <ConfirmButton
          actionText={messages.commonConfirm}
          buttonSize={
            {
              small: 'small',
              medium: 'small',
              large: 'medium',
              huge: 'medium'
            }[currentSettings.fontSize] as ButtonSize
          }
          buttonText={messages.commonTryAgain}
          confirmText={messages.readerCompletionBackWarning}
          confirmTitle={messages.commonTryAgain}
          onConfirm={handleRetry}
          stopPropagation={true}
        />
      ) : (
        <Button
          onClick={async () => {
            try {
              await handleRetry();
            } catch (err) {
              await dispatch.handleErrorDispatch(err);
            }
          }}
          size={
            {
              small: 'small',
              medium: 'small',
              large: 'medium',
              huge: 'medium'
            }[currentSettings.fontSize] as ButtonSize
          }
          text={messages.commonTryAgain}
        />
      )}
    </ReaderCompletionNotification>
  );
};

export default ReaderCompletionRetry;
